import type { Brand } from "./application";

export interface SummaryDepositsMetrics {
  current: SummaryDepositsItem;
  minus_7_days: SummaryDepositsItem;
  minus_14_days: SummaryDepositsItem;
}

export interface SummaryDepositsItem {
  date: string;
  records: SummaryDepositsItemRecord[];
}

export interface SummaryDepositsItemRecord {
  brand: Brand;
  date_time: string;
  conversion: string;
  value_approved: string;
  value_pending: string;
  value_total: string;
  pending_tax: string;
  accumulated_conversion: string;
  accumulated_value_approved: string;
  accumulated_value_pending: string;
  accumulated_value_total: string;
  accumulated_pending_tax: string;
}

export interface GgrMetrics {
  current: GgrItem;
  minus_7_days: GgrItem;
  minus_14_days: GgrItem;
}

export interface GgrItem {
  date: string;
  records: GgrItemRecord[];
}

export interface GgrItemRecord {
  accumulated_award_count: number;
  accumulated_award_value: number;
  accumulated_bet_count: number;
  accumulated_bet_value: number;
  accumulated_ggr: number;
  brand: string;
  date_time: string;
  total_award_count: number;
  total_award_value: number;
  total_bet_count: number;
  total_bet_value: number;
  total_ggr: number;
  type: string;
}

export interface SummaryGgrDetailsDaily {
  current_day: SummaryGgrDetailsDailyItem;
  last_30_days: SummaryGgrDetailsDailyItem[];
}

export interface SummaryGgrDetailsDailyItem {
  data: SummaryGgrDetailsData;
  date: string;
  day_of_week: string;
  period: string;
}

export interface SummaryGgrDetailsWeekly {
  current_week: SummaryGgrDetailsWeeklyItem;
  previous_week_1: SummaryGgrDetailsWeeklyItem;
  previous_week_2: SummaryGgrDetailsWeeklyItem;
  projection_next_week: SummaryGgrDetailsWeeklyItem;
}

export interface SummaryGgrDetailsWeeklyItem {
  data: SummaryGgrDetailsData;
  period: string;
  start_date: string;
  end_date: string;
}

export interface SummaryGgrDetailsMonthly {
  current_month: SummaryGgrDetailsMonthlyItem;
  previous_month_1: SummaryGgrDetailsMonthlyItem;
  previous_month_2: SummaryGgrDetailsMonthlyItem;
  projection_next_month: SummaryGgrDetailsMonthlyItem;
}

export interface SummaryGgrDetailsMonthlyItem {
  data: SummaryGgrDetailsData;
  period: string;
  start_date: string;
  end_date: string;
}

export interface SummaryGgrDetailsData {
  accumulated_award_count: number;
  accumulated_award_value: number;
  accumulated_bet_count: number;
  accumulated_bet_value: number;
  accumulated_ggr: number;
  total_award_count: number;
  total_award_value: number;
  total_bet_count: number;
  total_bet_value: number;
  total_ggr: number;
  conversion_ggr_percentage: number;
}

export enum SummaryGgrDetailsPeriod {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

export enum SummaryDepositsType {
  ACCUMULATED = 'accumulated',
  INDIVIDUAL = 'individual',
}

export enum SummaryDepositsInterval {
  '10M' = '10m' ,
  '30M' = '30m',
  '1H' = '1h',
  '2H' = '2h',
  '4H' = '4h',
}

export enum DashboardType {
  PIX_CONVERSION_RATE = 'pix_conversion_rate',
  GGR = 'ggr'
}

export interface SummaryGgrDetailsTurnover {
  current_date: SummaryGgrDetailsTurnoverItem;
  date_minus_7: SummaryGgrDetailsTurnoverItem;
  date_minus_14: SummaryGgrDetailsTurnoverItem;
}

export interface SummaryGgrDetailsTurnoverItem {
  brand: Brand;
  date: string;
  date_formatted: string;
  casino: SummaryGgrDetailsTurnoverItemData;
  sports: SummaryGgrDetailsTurnoverItemData;
  total: SummaryGgrDetailsTurnoverItemData;
}

export interface SummaryGgrDetailsTurnoverItemData {
  day_totals: SummaryGgrDetailsTurnoverItemDataDayTotals;
  hourly_data: SummaryGgrDetailsTurnoverItemDataHourlyData[];
}

export interface SummaryGgrDetailsTurnoverItemDataDayTotals {
  total_bet_count: number;
  total_bet_value: number;
}

export interface SummaryGgrDetailsTurnoverItemDataHourlyData {
  hour: string;
  total_bet_count: number;
  total_bet_value: number;
}

export interface DashboardPeriod {
  start_date: string;
  end_date: string;
  formatted: string;
}

export interface SummaryGgrDetailsOperations {
  brand: Brand;
  period: DashboardPeriod;
  apostas: SummaryGgrDetailsOperationsData;
  aposta_media: SummaryGgrDetailsOperationsData;
  caixa_liquido: SummaryGgrDetailsOperationsData;
  depositos: SummaryGgrDetailsOperationsData;
  ftds: SummaryGgrDetailsOperationsData;
  ggr: SummaryGgrDetailsOperationsData;
  premios: SummaryGgrDetailsOperationsData;
  usuarios_ativos: SummaryGgrDetailsOperationsData;
}

export interface SummaryGgrDetailsOperationsData {
  value: number;
  variation: number;
  qty_ftds?: number;
  avg_ftd_value?: number;
}

export interface FinancialLifeCycle {
  filters: FinancialLifeCycleFilters;
  lifecycle: FinancialLifeCycleItem[];
}

export interface FinancialLifeCycleFilters {
  brand: string;
  schema: string;
  start_date: string;
  end_date: string;
}

export interface FinancialLifeCycleItem {
  categoria: FinancialLifeCycleItemCategory;
  qtd_jogadores: number;
  valor_total: number;
}

export enum FinancialLifeCycleItemCategory {
  ATIVO_REGULAR = "ATIVO_REGULAR",
  CHURN = "CHURN",
  NOVO_APOSTADOR = "NOVO_APOSTADOR",
  REATIVACAO = "REATIVACAO",
}

export enum FinancialChurnType {
  BETS = "bets",
  CHARGE = "charge",
}

export interface FinancialChurn {
  churn: {
    periodo_referencia: FinancialChurnPeriod;
    periodo_comparacao: FinancialChurnPeriod;
    total: FinancialChurnTotal;
  },
  filters: {
    brand: string;
    schema: string;
    churn_type: string;
  }
}

export interface FinancialChurnPeriod {
  mes: string;
  inicio: string;
  fim: string;
}

export interface FinancialChurnTotal {
  qtd_jogadores: number;
  total_depositos: number;
  total_apostas: number;
  total_premios: number;
  total_ggr: number;
  churn_percentage: number;
}

export interface FinancialChurnDetailed {
  periodo_referencia: FinancialChurnPeriod;
  periodo_comparacao: FinancialChurnPeriod;
  filters: {
    brand: string;
    schema: string;
    churn_type: string;
  };
  items: FinancialChurnDetailedItem[];
}

export interface FinancialChurnDetailedItem {
  churn_percentage: number;
  qtd_jogadores: number;
  total_apostas: number;
  total_depositos: number;
  total_ggr: number;
  total_premios: number;
  utm_source: string;
}

export interface TopCasinoGamesTop5 {
  brand: Brand;
  period: DashboardPeriod;
  top_5_games: TopCasinoGamesTop5Game[];
  total_games: number;
}

export interface TopCasinoGamesTop5Game {
  average_bet: number;
  game_id: number;
  game_name: string;
  provider: string;
  rank: number;
  total_bet_amount: number;
  total_bet_qty: number;
}

export interface TopSportsMarketsTop5 {
  brand: Brand;
  period: DashboardPeriod;
  top_5_markets: TopSportsMarketsTop5Game[];
  total_markets: number;
}

export interface TopSportsMarketsTop5Game {
  average_bet_value: number;
  market_name: string;
  rank: number;
  total_bet_count: number;
  total_bet_value: number;
}

export interface FinancialDepositAndWithdrawChart {
  start: string;
  end: string;
  total_entradas: number;
  total_saidas: number;
  total_entradas_por_periodo: number;
  total_saidas_por_periodo: number;
  labels: string[];
  series: FinancialDepositAndWithdrawChartSeries[];
}

export interface FinancialDepositAndWithdrawMetrics {
  avg_deposit: FinancialDepositAndWithdrawMetricsValue;
  avg_withdrawal: FinancialDepositAndWithdrawMetricsValue;
  deposits: FinancialDepositAndWithdrawMetricsTotal;
  net_deposit: FinancialDepositAndWithdrawMetricsValue;
  transactions: FinancialDepositAndWithdrawMetricsTransactions;
  withdrawals: FinancialDepositAndWithdrawMetricsTotal;
  filters: {
    days: number;
    brand: Brand;
    end: string;
    start: string;
  };
}

export interface FinancialDepositAndWithdrawMetricsValue {
  change_percentage: number;
  period_label: string;
  value: number;
}

export interface FinancialDepositAndWithdrawMetricsTotal {
  change_percentage: number;
  period_label: string;
  total: number;
}

export interface FinancialDepositAndWithdrawMetricsCount {
  change_percentage: number;
  period_label: string;
  count: number;
}

export interface FinancialDepositAndWithdrawMetricsTransactions {
  change_percentage: number;
  deposits_count: number;
  deposits_count_change_percentage: number;
  period_label: string;
  total: number;
  withdrawals_count: number;
  withdrawals_count_change_percentage: number;
}

export interface FinancialDepositAndWithdrawChartSeries {
  name: string;
  data: number[];
}

export interface FinancialExpensesByCategory {
  start: string;
  end: string;
  total: number;
  labels: string[];
  series: number[];
  categories: FinancialExpensesByCategoryCategory[];
}

export interface FinancialExpensesByCategoryCategory {
  id: string;
  title: string;
  total: number;
}

export interface FinancialInvoice {
  filters: {
    end: string;
    months: number;
    start: string;
  };
  paid: {
    deposited: number;
    not_deposited: number;
    total: number;
  };
  outstanding: {
    in_day: number;
    overdue: number;
    total: number;
  };
}

export interface FinancialProfitAndExpenses {
  brand: Brand;
  filters: {
    days: number;
    brand: Brand;
    end: string;
    start: string;
  };
  despesas: FinancialProfitAndExpensesData;
  lucro_liquido: FinancialProfitAndExpensesData;
  receita_bruta: FinancialProfitAndExpensesDataProfit;
}

export interface FinancialProfitAndExpensesData {
  change_percentage: number;
  period_label: string;
  total: number;
  total_contas: number;
  value: number;
  pagas: { valor: number; quantidade: number; };
  pendentes: { valor: number; quantidade: number; };
}

export interface FinancialProfitAndExpensesDataProfit extends FinancialProfitAndExpensesData {
  detalhamento_receita: FinancialProfitAndExpensesDetail;
}

export interface FinancialProfitAndExpensesDetail {
  contas_receber: number;
  depositos: number;
  saldo_apostadores: number;
  saques: number;
  taxas: number;
}

export interface FinancialSupportMetrics {
  brand: Brand;
  filters: {
    days: number;
    brand: Brand;
    end: string;
    start: string;
  };
  chamados_resolvidos: FinancialSupportMetricsTicketsSolved;
  media_diaria: FinancialSupportMetricsData;
  media_saques_aprovados_mes: FinancialSupportMetricsData;
  movimentacao_financeira: FinancialSupportMetricsData;
  saques_aprovados_24h: FinancialSupportMetricsData;
  tma: FinancialSupportMetricsTime;
}

export interface FinancialSupportMetricsData {
  change_percentage: number;
  period_label: string;
  previous_value: number;
  value: number;
}

export interface FinancialSupportMetricsTime {
  formatted: string;
  minutes: number
  period_label: string;
}

export interface FinancialSupportMetricsTicketsSolved {
  change_percentage: number;
  pendentes: number;
  period_label: string;
  previous_value: number;
  total: number;
  value: number;
  por_dia: FinancialSupportMetricsTicketsSolvedItem[];
  por_dia_mes_anterior: FinancialSupportMetricsTicketsSolvedItem[];
}

export interface FinancialSupportMetricsTicketsSolvedItem {
  date: string
  pending: number;
  resolved: number;
  total: number;
}

export interface DepositDailyMetricsItem {
  date_dep: string;
  qty_deps: number;
  value_deps: number;
  avg_dep: number;
}

export interface DepositDailyMetricsMeta {
  brand: string;
  date: string;
  days: number;
  total_qty: number;
  total_value: number;
  overall_avg: number;
}

export interface DepositDailyMetricsResponse {
  success: boolean;
  message: string;
  data: DepositDailyMetricsItem[];
  meta: DepositDailyMetricsMeta;
  code?: number;
  extra?: any;
}

export interface FtdDailyMetricsItem {
  date_ftd: string;
  ftd_qty: number;
  ftd_value: number;
  avg_ftd_value: number;
}

export interface FtdDailyMetricsMeta {
  brand: string;
  date: string;
  days: number;
  total_qty: number;
  total_value: number;
  overall_avg: number;
}

export interface FtdDailyMetricsResponse {
  success: boolean;
  message: string;
  data: FtdDailyMetricsItem[];
  meta: FtdDailyMetricsMeta;
  code?: number;
  extra?: any;
}
